import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import styles from './DigitalCardGenerator.module.css';
import { UserContext } from '../../contexts/UserContext';
import DigitalCard from './DigitalCard';
import CardExporter from './CardExporter';
import PrivacyEmailGenerator from './PrivacyEmailGenerator';

const DigitalCardGenerator = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useContext(UserContext);
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGeneratingBackground, setIsGeneratingBackground] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    title: '',
    bio: '',
  });
  
  // Signal weights (the new preference system)
  const [signalWeights, setSignalWeights] = useState({});
  
  // Card appearance
  const [cardImage, setCardImage] = useState('');
  const [visibilityMode, setVisibilityMode] = useState('public');
  
  // Privacy features
  const [useEmailForwarding, setUseEmailForwarding] = useState(true);
  const [forwardingEmail, setForwardingEmail] = useState('');
  
  // Advanced signal options with weights
  const signalCategories = {
    'Design Styles': [
      'minimal', 'brutalist', 'modern', 'classical', 'industrial', 
      'tropical modernism', 'japanese', 'scandinavian', 'mid-century',
      'postmodern', 'bauhaus', 'art deco', 'mediterranean'
    ],
    'Materials': [
      'concrete', 'wood', 'steel', 'glass', 'stone', 'ceramic', 
      'plaster', 'marble', 'brass', 'copper', 'leather', 'fabric',
      'bamboo', 'cork', 'terrazzo'
    ],
    'Colors': [
      'neutral', 'monochrome', 'earth tones', 'vibrant', 'pastel',
      'black', 'white', 'warm', 'cool', 'jewel tones', 'muted'
    ],
    'Moods': [
      'serene', 'dramatic', 'cozy', 'sophisticated', 'playful',
      'elegant', 'bold', 'intimate', 'grand', 'refined'
    ],
    'Influences': [
      'Luis Barragán', 'James Turrell', 'Tadao Ando', 'Carlo Scarpa',
      'John Pawson', 'Peter Zumthor', 'Kengo Kuma', 'Donald Judd',
      'Charlotte Perriand', 'Jean Prouvé', 'Dieter Rams'
    ]
  };
  
  // CRITICAL FIX: Initialize form data with existing user data
  useEffect(() => {
    if (user && !isInitialized) {
      console.log('🔧 Initializing DigitalCardGenerator with user data:', user);
      
      // Initialize form data with existing user data
      setFormData({
        displayName: user.displayName || '',
        email: user.email || '',
        title: user.title || '',
        bio: user.bio || '',
      });
      
      // Initialize signal weights from user's curation signature
      if (user.curationSignature?.signalWeights) {
        console.log('🎯 Loading existing signal weights:', user.curationSignature.signalWeights);
        setSignalWeights(user.curationSignature.signalWeights);
      }
      
      // Initialize card appearance
      if (user.cardImage) {
        console.log('🎨 Loading existing card image:', user.cardImage);
        setCardImage(user.cardImage);
      }
      
      if (user.visibilityMode) {
        console.log('👁️ Loading existing visibility mode:', user.visibilityMode);
        setVisibilityMode(user.visibilityMode);
      }
      
      // Initialize privacy settings
      if (user.forwardingEmail) {
        console.log('🛡️ Loading existing forwarding email:', user.forwardingEmail);
        setForwardingEmail(user.forwardingEmail);
        setUseEmailForwarding(true);
      }
      
      setIsInitialized(true);
      console.log('✅ DigitalCardGenerator initialization complete');
    }
  }, [user, isInitialized]);
  
  // Generate forwarding email on component mount
  useEffect(() => {
    if (formData.email && useEmailForwarding && !forwardingEmail) {
      const generated = PrivacyEmailGenerator.generateForwardingEmail(formData.email);
      setForwardingEmail(generated);
    }
  }, [formData.email, useEmailForwarding, forwardingEmail]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle signal weight changes
  const handleSignalWeight = (signal, weight) => {
    setSignalWeights(prev => ({
      ...prev,
      [signal]: weight.toString()
    }));
  };
  
  // Generate AI-style background based on preferences
  const generateSmartBackground = () => {
    setIsGeneratingBackground(true);
    
    // Analyze signal weights to determine aesthetic
    const topSignals = Object.entries(signalWeights)
      .filter(([_, weight]) => parseInt(weight) > 0)
      .sort((a, b) => parseInt(b[1]) - parseInt(a[1]))
      .slice(0, 3);
    
    // Generate background based on top signals
    let gradient = '';
    
    if (topSignals.some(([signal]) => signal.includes('minimal'))) {
      gradient = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)';
    } else if (topSignals.some(([signal]) => signal.includes('brutalist'))) {
      gradient = 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)';
    } else if (topSignals.some(([signal]) => signal.includes('modern'))) {
      gradient = 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #3b82f6 100%)';
    } else if (topSignals.some(([signal]) => signal.includes('classical'))) {
      gradient = 'linear-gradient(135deg, #78716c 0%, #a8a29e 50%, #d6d3d1 100%)';
    } else if (topSignals.some(([signal]) => signal.includes('industrial'))) {
      gradient = 'linear-gradient(135deg, #27272a 0%, #52525b 50%, #71717a 100%)';
    } else {
      // Eclectic/default
      gradient = 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)';
    }
    
    setTimeout(() => {
      setCardImage(gradient);
      setIsGeneratingBackground(false);
    }, 1000); // Simulate AI generation time
  };
  
  // Save digital card to Firebase
  const saveDigitalCard = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      if (!user?.uid) {
        throw new Error('You must be logged in to create a Digital Card');
      }
      
      // Generate forwarding email if needed (synchronous now)
      let finalForwardingEmail = '';
      if (useEmailForwarding) {
        finalForwardingEmail = forwardingEmail || PrivacyEmailGenerator.generateForwardingEmail(formData.email);
      }
      
      // Create card data
      const digitalCardData = {
        userId: user.uid,
        displayName: formData.displayName || 'Artifex User',
        title: formData.title,
        bio: formData.bio,
        email: formData.email,
        cardImage: cardImage || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        visibilityMode,
        forwardingEmail: finalForwardingEmail,
        curationSignature: {
          signalWeights,
          scoreThreshold: 0.7,
        },
        settings: {
          filterMode: 'curated',
          public: visibilityMode !== 'private',
          useAsFeedFilter: true
        },
        createdAt: user.createdAt || new Date(),
        updatedAt: new Date()
      };
      
      // Save to Firebase
      await setDoc(doc(db, 'users', user.uid), digitalCardData, { merge: true });
      
      // Store forwarding email separately if it was generated - FIXED AWAIT
      if (finalForwardingEmail && user.uid) {
        const emailStored = await PrivacyEmailGenerator.storeForwardingEmail(
          finalForwardingEmail, 
          formData.email, 
          user.uid
        );
        
        if (!emailStored) {
          console.warn('Failed to store forwarding email, but continuing...');
          // Don't fail the entire card creation if email storage fails
        }
      }
      
      // Update user context
      updateUser({ ...user, ...digitalCardData });
      
      // Navigate to profile
      navigate(`/profiles/${formData.email?.split('@')[0] || user.uid}`);
      
    } catch (error) {
      console.error('Error saving Digital Card:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Export current card design
  const handleExport = () => {
    const exportData = {
      name: formData.displayName || 'Artifex User',
      email: formData.email,
      forwardingEmail: useEmailForwarding ? forwardingEmail : null,
      title: formData.title,
      bio: formData.bio,
      username: formData.email?.split('@')[0] || 'user',
      userId: user?.uid || 'guest',
      curationSignature: { signalWeights },
      visibilityMode
    };
    
    CardExporter.exportToVCF(exportData);
  };
  
  // Get current card data for preview
  const getPreviewData = () => ({
    displayName: formData.displayName || 'Your Name',
    title: formData.title || 'Your Title',
    bio: formData.bio || 'Your bio will appear here...',
    email: formData.email,
    userId: user?.uid || 'preview',
    cardImage: cardImage || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    forwardingEmail: useEmailForwarding ? forwardingEmail : null,
    curationSignature: { signalWeights },
    visibilityMode
  });
  
  // Navigation
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));
  
  // Show loading state while initializing
  if (!isInitialized && user) {
    return (
      <div className={styles.generator}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading your Digital Card...</p>
        </div>
      </div>
    );
  }
  
  // Step 1: Personal Information
  const renderStep1 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h2>Personal Information</h2>
        <p>This information will appear on your Digital Card and help personalize your Artifex experience.</p>
      </div>
      
      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label htmlFor="displayName">Display Name</label>
          <input
            type="text"
            id="displayName"
            name="displayName"
            value={formData.displayName}
            onChange={handleInputChange}
            placeholder="Your display name"
            className={styles.input}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your@email.com"
            className={styles.input}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="title">Title or Profession</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g. Architect, Designer, Curator"
            className={styles.input}
          />
        </div>
        
        <div className={styles.formGroupWide}>
          <label htmlFor="bio">Bio</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="A brief description of your aesthetic philosophy and interests..."
            maxLength={136}
            className={styles.textarea}
          />
          <div className={styles.charCount}>{formData.bio.length}/136</div>
        </div>
        
        <div className={styles.privacySection}>
          <div className={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="useForwarding"
              checked={useEmailForwarding}
              onChange={() => setUseEmailForwarding(!useEmailForwarding)}
              className={styles.checkbox}
            />
            <label htmlFor="useForwarding">Enable privacy email forwarding</label>
          </div>
          
          {useEmailForwarding && forwardingEmail && (
            <div className={styles.forwardingInfo}>
              <div className={styles.forwardingEmail}>
                🛡️ Privacy Email: <strong>{forwardingEmail}</strong>
              </div>
              <p>Use this email for third-party services. Messages forward to your main email while protecting your privacy.</p>
            </div>
          )}
        </div>
      </div>
      
      <div className={styles.stepActions}>
        <div></div>
        <button className={styles.primaryButton} onClick={nextStep}>
          Next: Define Your Aesthetic
        </button>
      </div>
    </div>
  );
  
  // Step 2: Aesthetic Preferences
  const renderStep2 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h2>Define Your Aesthetic</h2>
        <p>Rate your preferences from -5 (avoid) to +5 (love). This powers your personalized content discovery.</p>
      </div>
      
      <div className={styles.signalCategories}>
        {Object.entries(signalCategories).map(([category, signals]) => (
          <div key={category} className={styles.signalCategory}>
            <h3>{category}</h3>
            <div className={styles.signalGrid}>
              {signals.map(signal => (
                <div key={signal} className={styles.signalItem}>
                  <label>{signal}</label>
                  <div className={styles.weightSelector}>
                    {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(weight => (
                      <button
                        key={weight}
                        type="button"
                        className={`${styles.weightButton} ${
                          signalWeights[signal] === weight.toString() ? styles.selected : ''
                        } ${weight < 0 ? styles.negative : weight > 0 ? styles.positive : styles.neutral}`}
                        onClick={() => handleSignalWeight(signal, weight)}
                      >
                        {weight > 0 ? `+${weight}` : weight}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className={styles.stepActions}>
        <button className={styles.secondaryButton} onClick={prevStep}>
          Back
        </button>
        <button className={styles.primaryButton} onClick={nextStep}>
          Next: Card Design
        </button>
      </div>
    </div>
  );
  
  // Step 3: Card Design & Privacy
  const renderStep3 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h2>Card Design & Privacy</h2>
        <p>Customize your card's appearance and privacy settings.</p>
      </div>
      
      <div className={styles.designSection}>
        <div className={styles.designControls}>
          <div className={styles.visibilitySection}>
            <h3>Visibility Mode</h3>
            <div className={styles.visibilityOptions}>
              {['public', 'private', 'professional'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  className={`${styles.visibilityButton} ${visibilityMode === mode ? styles.selected : ''}`}
                  onClick={() => setVisibilityMode(mode)}
                >
                  <div className={styles.visibilityIcon}>
                    {mode === 'public' && '🌐'}
                    {mode === 'private' && '🔒'}
                    {mode === 'professional' && '💼'}
                  </div>
                  <div>
                    <strong>{mode.charAt(0).toUpperCase() + mode.slice(1)}</strong>
                    <p>
                      {mode === 'public' && 'Visible to all Artifex users'}
                      {mode === 'private' && 'Hidden from other users'}
                      {mode === 'professional' && 'Enhanced for business connections'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          <div className={styles.backgroundSection}>
            <h3>Card Background</h3>
            <p>Generate a background based on your aesthetic preferences</p>
            <button
              className={styles.generateButton}
              onClick={generateSmartBackground}
              disabled={isGeneratingBackground}
            >
              {isGeneratingBackground ? (
                <>
                  <div className={styles.spinner}></div>
                  Generating...
                </>
              ) : (
                'Generate AI Background'
              )}
            </button>
            
            {cardImage && (
              <div className={styles.backgroundPreview}>
                <div 
                  className={styles.backgroundSample}
                  style={{ background: cardImage }}
                ></div>
                <button 
                  className={styles.regenerateButton}
                  onClick={generateSmartBackground}
                >
                  Generate New
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.previewSection}>
          <h3>Live Preview</h3>
          <div className={styles.cardPreview}>
            <DigitalCard
              userData={getPreviewData()}
              size="medium"
              interactive={false}
            />
          </div>
          
          <div className={styles.previewActions}>
            <button className={styles.exportButton} onClick={handleExport}>
              Export VCF
            </button>
          </div>
        </div>
      </div>
      
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
      
      <div className={styles.stepActions}>
        <button className={styles.secondaryButton} onClick={prevStep}>
          Back
        </button>
        <button 
          className={styles.createButton}
          onClick={saveDigitalCard}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className={styles.spinner}></div>
              {isInitialized && (user?.title || user?.bio) ? 'Updating Your Digital Card...' : 'Creating Your Digital Card...'}
            </>
          ) : (
            isInitialized && (user?.title || user?.bio) ? 'Update Digital Card' : 'Create Digital Card'
          )}
        </button>
      </div>
    </div>
  );
  
  return (
    <div className={styles.generator}>
      <div className={styles.header}>
        <h1>{isInitialized && (user?.title || user?.bio) ? 'Edit Your Digital Card' : 'Create Your Digital Card'}</h1>
        <p>Your aesthetic passport for personalized content discovery</p>
      </div>
      
      <div className={styles.stepIndicator}>
        {[1, 2, 3].map(step => (
          <div 
            key={step}
            className={`${styles.stepDot} ${currentStep === step ? styles.active : ''} ${currentStep > step ? styles.complete : ''}`}
          >
            <span>{step}</span>
            <div className={styles.stepLabel}>
              {step === 1 && 'Personal Info'}
              {step === 2 && 'Aesthetic Profile'}  
              {step === 3 && 'Card Design'}
            </div>
          </div>
        ))}
      </div>
      
      <div className={styles.stepContainer}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default DigitalCardGenerator;